import { useMemo, useState } from "react";
import styles from "./index.module.scss";
import { ModalBase } from "../ModalBase";
import { useTranslation } from "react-i18next";
import {
    AssetCreateUpdateRequest,
    AssetResponse
} from "@switcherstudio/api-core-client";
import { useCallback } from "react";
import { closeCurrentModal } from "store/modal/slice";
import { AppDispatch } from "store/store";
import { useDispatch } from "react-redux";
import { displayDate } from "helpers/time";
import { displayFileSize } from "helpers/display";
import { TextInput } from "components/inputs/text-input/TextInput";
import { useProductionGroupsAssignment } from "hooks/useProductionGroupsAssignment";
import { BaseAutocomplete } from "components/inputs/autocomplete/base-autocomplete";
import { AssetThumbnail } from "components/thumbnails/AssetThumbnail";

export interface AssetEditModalProps {
    asset: AssetResponse;
    sourceTab?: "all-assets" | "raw-files";
    allProductionGroups: string[];
    getAssetById: (id: string) => AssetResponse | undefined;
    saveAsset: ([boolean, AssetCreateUpdateRequest]) => Promise<
        AssetResponse[]
    >;
    refetchAssets: () => void;
}

export const AssetEditModal = ({
    asset,
    sourceTab,
    allProductionGroups,
    getAssetById,
    saveAsset,
    refetchAssets
}: AssetEditModalProps) => {
    const { t } = useTranslation();
    const dispatch = useDispatch<AppDispatch>();

    const [loading, setLoading] = useState<boolean>(false);
    const [localName, setLocalName] = useState<string>(
        asset.Details?.Name || ""
    );

    const isRawFile = useMemo(() => {
        return sourceTab === "raw-files";
    }, [sourceTab]);

    const closeModal = useCallback(() => {
        dispatch(closeCurrentModal());
    }, [dispatch]);

    const {
        allProductionGroupsAsObjects,
        selectedProductionGroupsAsObjects,
        handleAddProductionGroup,
        handleAssignProductionGroup,
        handleRemoveProductionGroup
    } = useProductionGroupsAssignment({
        allProductionGroups,
        preselectedProductionGroups: asset.Details?.Tags || []
    });

    const relatedAssets = useMemo(() => {
        if (
            isRawFile &&
            asset.RequiredByAssetIds &&
            asset.RequiredByAssetIds.length > 0
        ) {
            return asset.RequiredByAssetIds.map((id) => {
                const requiredAsset = getAssetById(id);
                if (!requiredAsset) return <></>;
                return (
                    <AssetSummary
                        key={id}
                        asset={requiredAsset}
                        isRawFile={false}
                    />
                );
            });
        } else if (
            !isRawFile &&
            asset.RequiresAssetIds &&
            asset.RequiresAssetIds.length > 0
        ) {
            return asset.RequiresAssetIds.map((id) => {
                const requiredAsset = getAssetById(id);
                if (!requiredAsset) return <></>;
                return (
                    <AssetSummary
                        key={id}
                        asset={requiredAsset}
                        isRawFile={true}
                    />
                );
            });
        } else {
            return <></>;
        }
    }, [asset, getAssetById, isRawFile]);

    return (
        <div>
            <ModalBase
                isOpen={true}
                setIsOpen={closeModal}
                header={
                    isRawFile
                        ? t("cloud-assets:edit-raw")
                        : t("cloud-assets:edit-asset")
                }
                primaryButton={{
                    text: t("buttons:save"),
                    onClick: async () => {
                        setLoading(true);
                        const newTags = selectedProductionGroupsAsObjects.map(
                            (group) => group.Id
                        );
                        const updatedAsset: AssetCreateUpdateRequest = {
                            Id: asset.Details?.Id,
                            Name: localName,
                            Tags: newTags,
                            // Preserve the existing pair relation. The API
                            // treats this update as a full replace, so
                            // omitting it here would silently clear the
                            // link between the .mmart/.mmsrc and its raw
                            // file, breaking app-side group import.
                            RequiresAssetIds: asset.RequiresAssetIds || []
                        };

                        // Each upload creates a paired asset (e.g. the raw
                        // file + its .mmart/.mmsrc counterpart) that share
                        // production group tags. Re-tag the whole pair so
                        // they don't drift apart. The link between the pair
                        // is one-directional in each field: the .mmart
                        // points at the raw file via RequiresAssetIds, while
                        // the raw file points back at the .mmart via
                        // RequiredByAssetIds. Union both so the pair is
                        // found no matter which side is being edited.
                        const siblingIds = Array.from(
                            new Set([
                                ...(asset.RequiresAssetIds || []),
                                ...(asset.RequiredByAssetIds || [])
                            ])
                        ).filter((id) => id && id !== asset.Details?.Id);

                        const pairedAssets: AssetCreateUpdateRequest[] =
                            siblingIds
                                .map((id) => getAssetById(id))
                                .filter(
                                    (
                                        pairedAsset
                                    ): pairedAsset is AssetResponse =>
                                        !!pairedAsset
                                )
                                .map((pairedAsset) => ({
                                    Id: pairedAsset.Details?.Id,
                                    Name: pairedAsset.Details?.Name,
                                    Tags: newTags,
                                    // Same as above: preserve the
                                    // paired asset's own relation
                                    // fields so this save doesn't
                                    // clear them.
                                    RequiresAssetIds:
                                        pairedAsset.RequiresAssetIds ||
                                        []
                                }));

                        await saveAsset([
                            false,
                            [updatedAsset, ...pairedAssets]
                        ]);
                        closeModal();
                        refetchAssets();
                        setLoading(false);
                    }
                }}
                secondaryButton={{
                    text: t("buttons:cancel"),
                    onClick: closeModal
                }}
                loading={loading}
            >
                <form className={styles["asset-edit-form"]}>
                    <h6>Asset Details</h6>
                    <AssetSummary asset={asset} isRawFile={isRawFile} />
                    <TextInput
                        id="name"
                        type="name"
                        label={t("switcher-cloud:name")}
                        value={localName}
                        onChange={(e) => setLocalName(e.target.value)}
                    />
                    {relatedAssets && (
                        <div className={styles["related-assets"]}>
                            {relatedAssets}
                        </div>
                    )}
                    {!isRawFile && (
                        <fieldset>
                            <legend className="sr-only">
                                {t("cloud-assets:production-group")}
                            </legend>
                            <BaseAutocomplete
                                id={asset.Details?.Id}
                                assignedItems={
                                    selectedProductionGroupsAsObjects
                                }
                                handleAddItem={handleAddProductionGroup}
                                handleAssignItem={handleAssignProductionGroup}
                                handleRemoveItem={handleRemoveProductionGroup}
                                itemIdKey="Id"
                                itemLabelKey="Name"
                                items={allProductionGroupsAsObjects}
                                variant="add"
                                labels={{
                                    appliedItemsLabel: t(
                                        "cloud-assets:production-group"
                                    ),
                                    noItemsAppliedLabel: t(
                                        "cloud-assets:no-production-groups"
                                    ),
                                    inputLabel: t("cloud-assets:new-group"),
                                    addLabel: t("cloud-assets:add")
                                }}
                            />
                        </fieldset>
                    )}
                </form>
            </ModalBase>
        </div>
    );
};

const AssetSummary = ({
    asset,
    isRawFile
}: {
    asset: AssetResponse;
    isRawFile: boolean;
}) => {
    const { t } = useTranslation();
    const size = displayFileSize(asset.Details?.Size);

    return (
        <figure>
            <AssetThumbnail
                Url={
                    isRawFile ? asset.Details?.Url : asset.Details?.ThumbnailUrl
                }
            />
            <figcaption>
                {!isRawFile ? (
                    <>
                        <p>
                            {t("switcher-cloud:last-updated")}:{" "}
                            {displayDate(asset.Details?.UpdatedAt)}
                        </p>
                    </>
                ) : (
                    <>
                        <p>{t("switcher-cloud:raw-file")}</p>
                        <p>{asset.Details?.Name}</p>
                    </>
                )}
                <p>
                    {t("switcher-cloud:size")}: {size}
                </p>
            </figcaption>
        </figure>
    );
};
